import isEmptyObject from '../../../../utils/isEmptyObject';

/**
 * Creates a simple redux reducer. This is the base for other specialized
 * types of reducers.
 *
 * @param {Object} options The options for the creator function
 * @param {string} options.initialState The initial state of the reducer
 * @param {Object} options.config The handlers for each supported action.
 * @returns {Function} A newly created redux reducer
 *
 * @throws {Error} if `options` is not provided or not an object
 * @throws {Error} if `options.config` does not have at least 1 property
 *
 * @public
 * @author Connor Taylor
 * @example
 * import { combineReducers } from 'redux';
 *
 * const reducer = combineReducers({
 *   foo: makePlainReducer({
 *     initialState: ``,
 *     config: {
 *       SOME_ACTION: (state, action) => `foo`,
 *     },
 *   }),
 * });
 */
const makePlainReducer = ({ initialState, config }) => {
  if (typeof config !== `object`) {
    throw new Error(
      `makePlainReducer received a config that wasn't an object!`,
    );
  }
  if (isEmptyObject(config)) {
    throw new Error(`makePlainReducer received an empty config object!`);
  }

  return (state = initialState, action = {}) => {
    const handler = config[action.type];

    if (handler) {
      return handler(state, action);
    }

    return state;
  };
};

/** @component */
export default makePlainReducer;
